const CALCULATOR_SIGNAL_NAMES = Object.freeze({
  active: ["calculator.server.field.active", "Active fishing"],
  applyTradeModifiers: ["calculator.server.helper.apply_trade_settings", "Apply trade settings"],
  backpack: ["calculator.server.field.backpack", "Backpack"],
  brand: ["calculator.server.field.brand", "Brand"],
  buff: ["calculator.server.field.buffs", "Buffs"],
  catchTimeActive: ["calculator.server.field.active", "Active catch time"],
  catchTimeAfk: ["calculator.server.field.afk", "AFK catch time"],
  chair: ["calculator.server.field.chair", "Chair"],
  discardGrade: ["calculator.server.field.discard_grade", "Discard fish up to grade"],
  fishingMode: ["calculator.server.field.fishing_mode", "Fishing mode"],
  float: ["calculator.server.field.float", "Float"],
  food: ["calculator.server.field.food", "Food"],
  harpoonZone: ["calculator.server.field.harpoon_zone", "Harpooning zone"],
  hotspot: ["calculator.server.field.hotspot", "Hotspot"],
  level: ["calculator.server.field.fishing_level", "Fishing level"],
  lifeskill_level: ["calculator.server.field.lifeskill_level", "Lifeskill level"],
  lightstone_set: ["calculator.server.field.lightstone_set", "Lightstone set"],
  mastery: ["calculator.server.field.mastery", "Mastery"],
  outfit: ["calculator.server.field.outfit", "Outfit"],
  resources: ["calculator.server.field.fishing_resources", "Fishing Resources"],
  rod: ["calculator.server.field.fishing_rod", "Fishing rod"],
  rodZone: ["calculator.server.field.rod_zone", "Rod fishing zone"],
  targetFish: ["calculator.server.field.target_fish", "Target fish / loot item"],
  targetFishAmount: ["calculator.server.field.target_amount", "Target amount"],
  targetFishPmfCount: ["calculator.server.field.pmf_max_count", "PMF max count"],
  timespanAmount: ["calculator.server.field.amount", "Session amount"],
  timespanUnit: ["calculator.server.field.unit", "Session unit"],
  tradeDestinationNpc: ["calculator.server.field.trade_destination", "Destination trader"],
  tradeDistanceBonus: ["calculator.server.field.distance_bonus", "Distance bonus"],
  tradeOriginRegion: ["calculator.server.field.trade_origin", "Trade origin"],
  tradePriceCurve: ["calculator.server.field.trade_price_curve", "Trade price curve"],
  trade_level: ["calculator.server.field.trade_level", "Trade level"],
});

const PET_FIELD_NAMES = Object.freeze({
  packLeader: ["calculator.server.field.pack_leader", "Pack leader"],
  pet: ["calculator.server.field.pet", "Pet"],
  skills: ["calculator.server.field.skills", "Skills"],
  special: ["calculator.server.field.special", "Special"],
  talent: ["calculator.server.field.talent", "Talent"],
  tier: ["calculator.server.field.tier", "Tier"],
});

const PERCENT_SIGNALS = new Set(["resources", "tradeDistanceBonus", "tradePriceCurve"]);
const BOOLEAN_SIGNALS = new Set(["active", "applyTradeModifiers", "brand"]);
const ZONE_SIGNALS = new Set(["rodZone", "harpoonZone"]);
const SCOPED_SIGNAL_NAMES = Object.freeze({
  active: ["calculator.server.section.session", "Session", "calculator.server.field.active", "Active fishing"],
  catchTimeActive: ["calculator.server.section.catch_time", "Catch time", "calculator.server.field.active", "Active"],
  catchTimeAfk: ["calculator.server.section.catch_time", "Catch time", "calculator.server.field.afk", "AFK"],
  timespanAmount: ["calculator.server.section.session", "Session", "calculator.server.field.amount", "Amount"],
  timespanUnit: ["calculator.server.section.session", "Session", "calculator.server.field.unit", "Unit"],
});

function translate(key, fallback) {
  const helper = globalThis.window?.__fishystuffLanguage;
  if (!key || typeof helper?.t !== "function") {
    return fallback;
  }
  const value = helper.t(key);
  return !value || value === key ? fallback : value;
}

function signalPath(change) {
  const segments = Array.isArray(change?.pathSegments) ? change.pathSegments : [];
  if (
    segments[0]?.kind !== "property"
    || segments[0].name !== "signals"
    || segments[1]?.kind !== "property"
  ) {
    return null;
  }
  const remainder = segments.slice(2).map((segment) => (
    segment?.kind === "property"
      ? segment.name
      : segment?.kind === "index"
        ? `[${segment.index}]`
        : ""
  )).filter(Boolean).join(".").replace(/\.\[/g, "[");
  return {
    key: segments[1].name,
    remainder,
    remainderSegments: segments.slice(2),
  };
}

function humanize(value) {
  const text = String(value ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "Setting";
}

function controlForSignal(key, root = globalThis.document) {
  return /^[A-Za-z0-9_.]+$/.test(key)
    ? root?.querySelector?.(`[data-bind="${key}"]`) ?? null
    : null;
}

function controlLabel(control) {
  const ariaLabel = String(control?.getAttribute?.("aria-label") ?? "").trim();
  if (ariaLabel) {
    return ariaLabel;
  }
  const fieldset = control?.closest?.("fieldset");
  const legend = String(fieldset?.querySelector?.(":scope > legend")?.textContent ?? "").trim();
  if (legend) {
    return legend;
  }
  const label = control?.closest?.("label");
  return String(label?.textContent ?? "").replace(/\s+/g, " ").trim();
}

function calculatorSignalName(path, root) {
  const nestedProperty = path.remainderSegments.find((segment) => segment?.kind === "property")
    ?.name ?? "";
  const nestedKey = nestedProperty ? `${path.key}.${nestedProperty}` : path.key;
  const pet = path.key.match(/^pet([1-5])$/);
  if (pet) {
    const field = nestedProperty;
    const [fieldKey, fieldFallback] = PET_FIELD_NAMES[field]
      ?? ["", humanize(field || "Pet")];
    return `${translate("calculator.server.field.pet", "Pet")} ${pet[1]} · ${translate(fieldKey, fieldFallback)}`;
  }
  const scoped = SCOPED_SIGNAL_NAMES[path.key];
  if (scoped) {
    return `${translate(scoped[0], scoped[1])} · ${translate(scoped[2], scoped[3])}`;
  }
  const fromControl = controlLabel(controlForSignal(nestedKey, root))
    || controlLabel(controlForSignal(path.key, root));
  if (fromControl) {
    return fromControl;
  }
  const [key, fallback] = CALCULATOR_SIGNAL_NAMES[path.key]
    ?? ["", humanize(path.key)];
  return translate(key, fallback);
}

function rgbTriplet(value) {
  const parts = String(value ?? "").split(",").map((part) => Number.parseInt(part.trim(), 10));
  return parts.length === 3 && parts.every((part) => Number.isInteger(part) && part >= 0 && part <= 255)
    ? parts.join(" ")
    : "";
}

function optionText(control, value) {
  const normalized = String(value ?? "");
  const option = Array.from(control?.options ?? []).find((candidate) => candidate.value === normalized);
  if (option) {
    return String(option.textContent ?? "").replace(/\s+/g, " ").trim();
  }
  const root = control?.closest?.("[data-searchable-dropdown]") ?? control?.parentElement;
  const candidates = Array.from(root?.querySelectorAll?.("[data-value]") ?? []);
  const match = candidates.find((candidate) => candidate.getAttribute?.("data-value") === normalized);
  return String(
    match?.querySelector?.(".fishy-zone-label, [data-role='option-content']")?.textContent
      ?? match?.textContent
      ?? "",
  ).replace(/\s+/g, " ").trim();
}

function zoneName(key, value, root, control) {
  const normalized = String(value ?? "");
  const pickerId = key === "harpoonZone"
    ? "calculator-harpoon-zone-picker"
    : "calculator-rod-zone-picker";
  const picker = root?.getElementById?.(pickerId) ?? root?.querySelector?.(`#${pickerId}`);
  const candidates = Array.from(picker?.querySelectorAll?.("[data-value]") ?? []);
  const option = candidates.find((candidate) => candidate.getAttribute?.("data-value") === normalized);
  const optionLabel = option?.querySelector?.(".fishy-zone-label")?.textContent;
  if (String(optionLabel ?? "").trim()) {
    return String(optionLabel).trim();
  }
  if (String(control?.value ?? "") === normalized) {
    const selectedLabel = picker?.querySelector?.(".fishy-zone-label")?.textContent;
    if (String(selectedLabel ?? "").trim()) {
      return String(selectedLabel).trim();
    }
  }
  return optionText(control, value) || normalized;
}

function plainValue(value, present) {
  if (!present) {
    return "—";
  }
  if (value === null) {
    return "None";
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? "None" : value.map((entry) => String(entry)).join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

function presentValue(path, value, present, root) {
  if (!present) {
    return { kind: "text", text: "—" };
  }
  const control = controlForSignal(path.key, root);
  if (ZONE_SIGNALS.has(path.key)) {
    return {
      kind: "zone",
      rgb: rgbTriplet(value),
      text: zoneName(path.key, value, root, control),
    };
  }
  if (BOOLEAN_SIGNALS.has(path.key) && typeof value === "boolean") {
    return { kind: "text", text: value ? "On" : "Off" };
  }
  if (path.key === "fishingMode") {
    return {
      kind: "text",
      text: value === "harpoon" ? "Harpoon" : value === "hotspot" ? "Hotspot" : "Rod",
    };
  }
  const option = optionText(control, value);
  if (option) {
    return { kind: "text", text: option };
  }
  if (PERCENT_SIGNALS.has(path.key) && typeof value === "number") {
    return { kind: "text", text: `${value}%` };
  }
  return { kind: "text", text: plainValue(value, true) };
}

export function presentCalculatorPresetChange(change, root = globalThis.document) {
  const path = signalPath(change);
  if (!path) {
    return null;
  }
  return {
    name: calculatorSignalName(path, root),
    signalKey: path.remainder ? `${path.key}.${path.remainder}` : path.key,
    oldValue: presentValue(path, change.savedValue, change.savedPresent, root),
    newValue: presentValue(path, change.workingValue, change.workingPresent, root),
  };
}
